import { useCallback, useEffect, useRef, useState } from 'react';
import { Virtuoso, type VirtuosoHandle } from 'react-virtuoso';
import type { Message } from '@shared/types';
import { ErrorState, LoadingBar } from '@web/components/Feedback';
import { LiveRegion, useThrottledAnnouncement } from '@web/components/LiveRegion';
import { isSameDay } from '@web/lib/time';
import { DayDivider, MessageBubble } from './MessageBubble';
import { useFlatMessages, useMessages } from './useThread';

/**
 * The transcript.
 *
 * Built on the MIT `Virtuoso` rather than `@virtuoso.dev/message-list`, which is
 * a commercial per-developer licence and renders an error banner in production
 * once the trial lapses. The forty extra lines here are `firstItemIndex`
 * bookkeeping and they buy a dependency that cannot expire.
 *
 * The two behaviours that matter:
 *  - `followOutput` as a FUNCTION returning false when the reader is not at the
 *    bottom. Stick-to-bottom must never fight someone scrolling up through
 *    history — that is the single most infuriating bug a chat UI can have.
 *  - `firstItemIndex` decreasing in the SAME render as the data grows. Both are
 *    derived from one query result, so they cannot land in two renders and snap
 *    the scroll position.
 */

/** Virtual index space, so `firstItemIndex` can keep counting down all shift. */
const START_INDEX = 10_000_000;

export interface TranscriptHandle {
  scrollToBottom: () => void;
}

export function Transcript({
  conversationId,
  handleRef,
  authorNames,
}: {
  conversationId: string;
  handleRef?: React.RefObject<TranscriptHandle | null>;
  /** userId → display name, so an agent bubble can be attributed. */
  authorNames?: Record<string, string>;
}) {
  const virtuoso = useRef<VirtuosoHandle>(null);
  const { data, error, isPending, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useMessages(conversationId);
  const { messages, prepended } = useFlatMessages(data?.pages);

  const [atBottom, setAtBottom] = useState(true);
  const [unseen, setUnseen] = useState(0);
  const previousCount = useRef(0);
  const previousLast = useRef<string | null>(null);

  // Count what arrived while the reader was up in the history, so the pill can
  // say how much they have not seen.
  useEffect(() => {
    const grew = messages.length - previousCount.current;
    previousCount.current = messages.length;
    if (grew > 0 && !atBottom) setUnseen((count) => count + grew);
  }, [messages.length, atBottom]);

  const newest = messages.at(-1);
  const announcement =
    newest !== undefined && newest.id !== previousLast.current && newest.senderType === 'contact'
      ? `New message: ${newest.body ?? 'attachment'}`
      : '';
  // Throttled and scoped to the OPEN thread. Nothing announces for a
  // conversation nobody is looking at.
  const announced = useThrottledAnnouncement(announcement, 3000);
  useEffect(() => {
    previousLast.current = newest?.id ?? null;
  }, [newest?.id]);

  const scrollToBottom = useCallback(() => {
    virtuoso.current?.scrollToIndex({ index: 'LAST', behavior: 'smooth' });
    setUnseen(0);
  }, []);

  useEffect(() => {
    if (handleRef === undefined) return;
    handleRef.current = { scrollToBottom };
  }, [handleRef, scrollToBottom]);

  const itemContent = useCallback(
    (index: number, message: Message) => {
      const position = index - (START_INDEX - prepended);
      const previous = position > 0 ? messages[position - 1] : undefined;
      const startsDay = previous === undefined || !isSameDay(previous.createdAt, message.createdAt);
      return (
        <>
          {startsDay && <DayDivider iso={message.createdAt} />}
          <MessageBubble
            message={message}
            authorName={message.senderId === null ? undefined : authorNames?.[message.senderId]}
          />
        </>
      );
    },
    [messages, prepended, authorNames],
  );

  if (isPending) return <LoadingBar label="Loading transcript" />;
  if (error !== null) return <ErrorState error={error} />;

  return (
    <div className="relative h-full">
      <LiveRegion text={announced} />
      <Virtuoso
        ref={virtuoso}
        data={messages}
        computeItemKey={(_, message) => message.id}
        firstItemIndex={START_INDEX - prepended}
        initialTopMostItemIndex={Math.max(messages.length - 1, 0)}
        alignToBottom
        followOutput={(isAtBottom) => (isAtBottom ? 'smooth' : false)}
        atBottomStateChange={(bottom) => {
          setAtBottom(bottom);
          if (bottom) setUnseen(0);
        }}
        atBottomThreshold={24}
        startReached={() => {
          if (hasNextPage && !isFetchingNextPage) void fetchNextPage();
        }}
        increaseViewportBy={{ top: 800, bottom: 200 }}
        itemContent={itemContent}
        className="h-full [overscroll-behavior:contain]"
        components={{
          Header: () =>
            isFetchingNextPage ? (
              <div className="px-4 py-3 text-center font-mono text-[10px] tracking-[0.1em] text-ink-faint">
                LOADING OLDER
              </div>
            ) : !hasNextPage && messages.length > 0 ? (
              <div className="px-4 py-3 text-center font-mono text-[10px] tracking-[0.1em] text-ink-faint">
                START OF CONVERSATION
              </div>
            ) : null,
        }}
      />

      {!atBottom && unseen > 0 && (
        <button
          type="button"
          onClick={scrollToBottom}
          className="absolute bottom-3 left-1/2 flex min-h-9 -translate-x-1/2 items-center gap-2 border border-signal bg-panel px-3 font-mono text-[11px] text-signal"
        >
          <span aria-hidden className="inline-block size-[6px] bg-signal" />
          {unseen} new
        </button>
      )}
    </div>
  );
}
